import { type ComponentPropsWithoutRef, forwardRef } from "react";

import { cn } from "@/lib/cn";

import Icon from "../Icon/Icon";
import type { ColorVariantType } from "../Icon/Icon";
import type { IconName } from "../Icon/iconMap";
import { Loader } from "../Loading/Loader";
import { TooltipWrapper } from "../Tooltip/TooltipWrapper";
import { Button, type ButtonProps } from "../ui/button";

// ボタンのvariantに基づいてアイコンのcolorVariantを決定するマッピング
const variantToIconColor: Record<
  NonNullable<ButtonProps["variant"]>,
  ColorVariantType
> = {
  default: "white", // bg-primary-fill text-primary-on-fill → 明示的に白色
  destructive: "white", // bg-error-fill text-error-on-fill → 明示的に白色
  outline: "neutral", // 初期状態はneutral、hover時にprimary
  secondary: "neutral", // bg-secondary-muted-fill text-secondary-foreground
  ghost: "neutral", // 初期状態はneutral、hover時にprimary
  mutedGhost: "neutral", // hover:text-on-muted-fill
  link: "primaryOnNeutral", // text-primary
  neutral: "neutral", // bg-neutral-fill
  linkPrimary: "primaryOnNeutral", // text-primary-on-neutral-fill
  danger: "error", // text-error-on-neutral-fill
  linkMutedPrimary: "primaryOnNeutralMuted", // text-primary-muted-on-neutral-fill
  accent: "white", // bg-accent-fill text-accent-on-fill → 明示的に白色
  mutedAccent: "neutral",
};

type Props = ButtonProps & {
  icon?: IconName;
  iconColor?: ColorVariantType;
  subIcon?: IconName;
  subIconColor?: ColorVariantType;
  size?: Exclude<ButtonProps["size"], "icon">;
  isLoading?: boolean;
  iconPosition?: "left" | "right" | "both";
  disabledDetail?: {
    message: string;
  };
};

const LoadingIcon = forwardRef<
  HTMLSpanElement,
  {
    size?: ComponentPropsWithoutRef<typeof Icon>["size"];
  } & JSX.IntrinsicElements["span"]
>(function LoadingIcon({ size: _size, className, ...props }, ref) {
  return (
    <span ref={ref} className={className} {...props}>
      <Loader size={`size-4`} variant="current" />
    </span>
  );
});

export default forwardRef<HTMLButtonElement, Props>(function ActionButton(
  {
    icon,
    iconColor: _iconColor,
    subIcon,
    subIconColor: _subIconColor,
    isLoading,
    className,
    children,
    size,
    iconPosition = "left",
    disabled,
    disabledDetail,
    variant,
    ...props
  },
  ref,
) {
  const iconSize = size === "sm" ? 14 : 16;

  // variantに基づいて自動的にアイコンの色を決定
  // variantが未指定の場合は、ボタンのデフォルトvariant="default"として扱う
  // 注意: iconColor、subIconColorの指定を無視するようにしています。（figmaに合わせた＆毎度指定する必要がないため）
  // to doとして、各所のActionButtonで、指定されているiconColorとsubIconColorを削除する、が残っています。
  // 後方互換性のためと、各所の修正をするのが困難だったので、暫定的な対応として、iconColor、subIconColorの指定を無視するようにしています。
  const computedIconColor = variantToIconColor[variant ?? "default"];

  /**
   * ローディングアイコンを表示する位置は、アイコンが設定されているかどうかで変わる
   * - アイコンが設定されている場合：アイコンをローディングアイコンに置き換える
   * - アイコンが設定されていない場合：childrenをローディングアイコンに置き換える
   */
  const content = (() => {
    // アイコンなしの場合は中央にローディング
    if (isLoading && !icon) {
      return (
        <>
          <LoadingIcon className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
          <span className="invisible">{children}</span>
        </>
      );
    }

    // 左側のコンテンツ
    const leftContent =
      iconPosition !== "right" &&
      (isLoading ? (
        <LoadingIcon />
      ) : (
        icon && (
          <Icon
            size={iconSize}
            iconName={icon}
            colorVariant={computedIconColor}
          />
        )
      ));

    // 右側のコンテンツ
    const rightContent =
      iconPosition === "right" ? (
        isLoading ? (
          <LoadingIcon />
        ) : (
          icon && (
            <Icon
              size={iconSize}
              iconName={icon}
              colorVariant={computedIconColor}
            />
          )
        )
      ) : (
        iconPosition === "both" &&
        subIcon && (
          <Icon
            size={iconSize}
            iconName={subIcon}
            colorVariant={computedIconColor}
          />
        )
      );

    return (
      <>
        {leftContent}
        {children}
        {rightContent}
      </>
    );
  })();

  const button = (
    <Button
      ref={ref}
      className={cn("relative gap-2", className)}
      disabled={isLoading || disabled}
      size={size}
      variant={variant}
      {...props}
    >
      {content}
    </Button>
  );

  // disabledでdisabledDetailが指定されている場合、ツールチップでメッセージを表示
  if (disabled && disabledDetail?.message) {
    return (
      <TooltipWrapper
        content={disabledDetail.message}
        side="top"
        containerClassName="inline-block cursor-not-allowed"
      >
        {button}
      </TooltipWrapper>
    );
  }

  return button;
});
